import type { DevRequest, DevRequestSpecLink } from '@/features/project-board/types';

/**
 * 개발요청 보드의 컬럼 — 요청 하나가 하네스 파이프라인에서 어디까지 갔나.
 *
 * 요청 문서 자체에는 상태 필드가 없다. 유일한 근거는 **그 요청을 가리키는 스펙의 단계**(색인이 PRD의
 * 개발요청 번호를 읽어 이어 준 것)라서, 컬럼은 스펙 단계를 요청 쪽 어휘로 접은 것이다. 스펙 단계를
 * 그대로 컬럼으로 쓰지 않는 이유: 요청을 보낸 사람은 `spec-review`·`sync` 같은 관문을 모른다.
 */
export interface DevRequestLane {
  key: string;
  label: string;
  /** 이 컬럼이 무엇을 뜻하는지 — 요청을 보낸 쪽이 읽는 문장이다. */
  hint: string;
}

export const DEV_REQUEST_LANES: readonly DevRequestLane[] = [
  { key: 'received', label: '접수', hint: '아직 스펙이 없다 — 착수 전' },
  { key: 'spec', label: '스펙 작성', hint: 'PRD·설계·task 분해 중' },
  { key: 'build', label: '구현', hint: '코드 작업 중' },
  { key: 'qa', label: 'QA', hint: 'QA 인계·검증 중' },
  { key: 'done', label: '완료', hint: '문서 동기화까지 끝났다' },
];

/** 한 요청과, 그 요청이 만들어 낸 스펙들. 화면이 카드 하나를 그리는 데 필요한 전부다. */
export interface DevRequestWithSpecs {
  request: DevRequest;
  specs: DevRequestSpecLink[];
}

/** 컬럼 하나와 거기 서 있는 요청들. */
export interface DevRequestLaneGroup {
  lane: DevRequestLane;
  items: DevRequestWithSpecs[];
}

export class DevRequestFlow {
  /**
   * 스펙 단계 → 요청 컬럼.
   *
   * **옛 키를 지우지 않는다.** 화면이 보는 단계 키는 색인이 저장해 둔 값이고, 색인은 문서의 사영이라
   * 코드보다 늦다 — 단계 키를 바꾸면 재색인이 돌기 전까지 옛 키가 그대로 들어온다. 실제로 `e2e`를 `qa`로
   * 바꿨을 때 재색인 전 스펙이 전부 미등록 키가 돼, 아래 fallback을 타고 **가장 앞 컬럼**으로 떨어졌다
   * (검수까지 끝난 요청이 "스펙 작성"에 서 있었다).
   *
   * 등록되지 않은 키의 fallback이 `spec`인 것은 카드를 잃지 않기 위해서지, 그게 맞는 위치라서가 아니다.
   * 단계를 추가하거나 이름을 바꾸면 **반드시 여기에 줄을 더한다.**
   */
  static readonly LANE_OF_STAGE: Record<string, string> = {
    prd: 'spec',
    design: 'spec',
    'spec-review': 'spec',
    tasks: 'spec',
    execution: 'build',
    // 리뷰는 아직 QA에 인계되기 전이다. QA 요청서나 E2E 결과가 생겨 `qa`로
    // 진입할 때까지는 요청자에게 개발 중으로 보여야 한다.
    review: 'build',
    qa: 'qa',
    // 옛 이름 — `qa`로 바뀌기 전 색인이 남긴 값. 재색인이 끝나도 지우지 않는다(옛 실행 기록이 되살아날 수 있다).
    e2e: 'qa',
    sync: 'done',
  };

  static laneIndex(key: string): number {
    return DEV_REQUEST_LANES.findIndex((lane) => lane.key === key);
  }

  /**
   * 요청이 서는 컬럼.
   *
   * 요청 하나가 여러 스펙으로 쪼개질 수 있고, 그럴 때는 **가장 덜 진행된 스펙**이 요청의 위치다 —
   * 요청을 보낸 쪽이 묻는 건 "내가 부탁한 게 다 끝났나"이므로, 하나라도 안 끝났으면 끝난 게 아니다.
   */
  static laneOf(specs: readonly DevRequestSpecLink[]): string {
    if (specs.length === 0) return 'received';

    return specs
      .map((spec) => DevRequestFlow.LANE_OF_STAGE[spec.stage] ?? 'spec')
      .reduce((slowest, lane) => (DevRequestFlow.laneIndex(lane) < DevRequestFlow.laneIndex(slowest) ? lane : slowest));
  }

  /**
   * 요청 목록을 컬럼별로 갈라 놓는다.
   *
   * 컴포넌트가 아니라 여기서 하는 이유는 두 가지다. 판정(`laneOf`)과 그 판정을 적용하는 일이
   * 같은 자리에 있어야 규칙이 한 곳에서 읽히고, React 없이 부를 수 있어야 시험할 수 있다.
   *
   * 스펙은 요청 id로 한 번만 갈라 놓고 쓴다 — 컬럼마다 전체 목록을 다시 훑으면 요청·스펙 수의 곱이 된다.
   */
  static groupIntoLanes(
    requests: readonly DevRequest[],
    specs: readonly DevRequestSpecLink[],
  ): DevRequestLaneGroup[] {
    const withSpecs = requests.map((request) => ({
      request,
      specs: specs.filter((spec) => spec.devRequestId === request.id),
    }));

    return DEV_REQUEST_LANES.map((lane) => ({
      lane,
      items: withSpecs.filter((entry) => DevRequestFlow.laneOf(entry.specs) === lane.key),
    }));
  }

  /**
   * 개발요청 화면에서 단계를 부르는 이름 — 색인이 준 라벨을 여기서만 갈아 끼운다.
   *
   * 이 화면은 두 축을 함께 보여준다. **컬럼**은 요청이 어디쯤 왔나(요청자 어휘 — 접수·스펙 작성·구현·QA·완료)이고,
   * **칩**은 그 스펙이 하네스 파이프라인의 어느 문서 단계에 있나다. 둘이 같은 이름이면 축이 하나로 보여서,
   * 칩은 문서 단계 이름(`E2E`)을 그대로 쓴다. 다른 보드(에픽·이슈·인시던트)는 색인 라벨(`QA`)을 그대로 쓴다.
   *
   * 여기 없는 단계는 색인 라벨을 그대로 쓴다.
   */
  static readonly STAGE_LABEL_ON_REQUEST: Record<string, string> = {
    qa: 'E2E',
    // 옛 키 — `LANE_OF_STAGE`와 같은 이유로 남긴다(재색인 전 색인이 준 값).
    e2e: 'E2E',
  };

  /** 스펙 한 건의 진행 표기 — 단계 이름에 진행률을 붙인다. task가 아직 없으면 진행률을 쓰지 않는다. */
  static specProgressLabel(spec: DevRequestSpecLink): string {
    const stage = DevRequestFlow.STAGE_LABEL_ON_REQUEST[spec.stage] ?? spec.stageLabel;
    return spec.donePct > 0 ? `${stage} · ${spec.donePct}%` : stage;
  }

  /**
   * 그 스펙의 에픽을 연 에픽 페이지 주소.
   *
   * 저장소 탭은 `source`로 맞추고, 에픽은 `kind=epic&item=`으로 드로어를 연다 — 목록 필터가 아니라 드로어를 쓰는 이유는,
   * `epic` 필터는 task 목록(보드·백로그)만 거르고 에픽 목록은 거르지 못해 링크를 눌러도 에픽이 전부 보이기 때문이다.
   * 별도 상세 라우트를 만들지 않는 이유는, 스펙을 보는 정본 화면이 이미 에픽 페이지의 드로어이기 때문이다.
   *
   * `item` 키는 `ItemKey.epic`(저장소#slug)과 같아야 한다. 이 파일은 노드 테스트가 직접 읽어 `@/` 별칭을 못 쓰므로 `ItemKey`를
   * 부르지 않고 같은 형식을 적었고, 어긋나지 않는지는 스펙이 확인한다.
   */
  static specBoardPath(spec: DevRequestSpecLink): string {
    return `/project-board?source=${encodeURIComponent(spec.source)}&kind=epic&item=${encodeURIComponent(`${spec.source}#${spec.slug}`)}`;
  }
}
